// App.jsx — top-level router host.
// Replaces the prior monolithic localStorage-based App.jsx with a thin shell
// that mounts AppShell (sidebar + top bar) and delegates each hash route to
// its own page module. Data lives in Supabase (see src/lib/db.js).

import React, { useEffect, useState } from 'react';
import ErrorBoundary from './components/ErrorBoundary';
import Toaster from './components/Toaster';
import AppShell from './components/layout/AppShell';
import { FirmProvider } from './contexts/FirmContext';
import { EncryptionProvider } from './contexts/EncryptionContext';
import { useFirm } from './contexts/FirmContext';
import { PermissionsProvider } from './contexts/PermissionsContext';
import { PlanProvider } from './contexts/PlanContext';
import { useAuth } from './contexts/AuthContext';
import { useImpersonation } from './contexts/ImpersonationContext';
import ImpersonationBanner from './components/ImpersonationBanner';
import AnnouncementsBanner from './components/AnnouncementsBanner';
import EmployeeProfileReminderBanner from './components/onboarding/EmployeeProfileReminderBanner';
import CommandPalette from './components/CommandPalette';
import FloatingTimer from './components/FloatingTimer';
import { TimerProvider } from './contexts/TimerContext';
import FirmOnboardingWizard from './pages/onboarding/FirmOnboardingWizard';
import PostOnboardingWelcome, { shouldShowPostOnboardingWelcome } from './pages/onboarding/PostOnboardingWelcome';
import SubscriptionSuspendedPage from './pages/SubscriptionSuspendedPage';
import EmployeeProfileWizard from './pages/EmployeeProfileWizard';
import { OnboardingHelpersProvider, OnboardingHost } from './components/onboarding/OnboardingHelpers';
import { useRoute, matchRoute } from './lib/router';
import { RefreshCw, WifiOff } from 'lucide-react';
import { fetchEmployeeByUserId, fetchEmployeeByEmpIdString, fetchEmployeeByEmail, ensureMyEmployeeRecord } from './lib/db';

import DashboardPage      from './pages/DashboardPage';
import MattersPage        from './pages/MattersPage';
import MatterDetailPage   from './pages/MatterDetailPage';
import ContactsPage       from './pages/ContactsPage';
import ContactDetailPage  from './pages/ContactDetailPage';
import CalendarPage       from './pages/CalendarPage';
import TimePage           from './pages/TimePage';
import BillingPage        from './pages/BillingPage';
import TrustPage          from './pages/TrustPage';
import IntakePage         from './pages/IntakePage';
import DocumentsPage      from './pages/DocumentsPage';
import DocumentTemplatesPage from './pages/DocumentTemplatesPage';
import ReportsPage        from './pages/ReportsPage';
import MessagesPage       from './pages/MessagesPage';
import SettingsPage       from './pages/SettingsPage';
import TasksPage          from './pages/TasksPage';
import ExpensesPage       from './pages/ExpensesPage';
import ActivityLogPage    from './pages/ActivityLogPage';
import DeadlinesPage      from './pages/DeadlinesPage';
import EmployeesPage      from './pages/EmployeesPage';
import EmployeeDetailPage from './pages/EmployeeDetailPage';
import ConflictCheckPage  from './pages/ConflictCheckPage';
import RolesPage          from './pages/RolesPage';
import BulkInviteTeamPage from './pages/onboarding/BulkInviteTeamPage';

function Router() {
  const [{ pathname, segments }] = useRoute();

  // Exact / longer paths first
  const matterDetail = matchRoute(segments, 'matters/:id');
  if (matterDetail) return <MatterDetailPage key={matterDetail.id} matterId={matterDetail.id} />;

  const contactDetail = matchRoute(segments, 'contacts/:id');
  if (contactDetail) return <ContactDetailPage key={contactDetail.id} contactId={contactDetail.id} />;

  // /employees/invite must be matched BEFORE the dynamic /employees/:id route
  // — otherwise `:id` swallows the literal "invite" segment.
  if (segments[0] === 'employees' && segments[1] === 'invite') return <BulkInviteTeamPage key="employees-invite" />;

  const employeeDetail = matchRoute(segments, 'employees/:id');
  if (employeeDetail) return <EmployeeDetailPage key={employeeDetail.id} employeeId={employeeDetail.id} />;

  // The `key` on each page component equals the current pathname.
  // This ensures that every navigation — including re-clicking the current
  // sidebar link — remounts the page and triggers a fresh data fetch.
  // Without this, navigating from /contacts → /matters → /contacts would
  // reuse the cached component and show stale data.
  const key = pathname;
  switch (pathname) {
    case '/':           return <DashboardPage    key={key} />;
    case '/matters':    return <MattersPage      key={key} />;
    case '/contacts':   return <ContactsPage     key={key} />;
    case '/calendar':   return <CalendarPage     key={key} />;
    case '/time':       return <TimePage         key={key} />;
    case '/billing':    return <BillingPage      key={key} />;
    case '/trust':      return <TrustPage        key={key} />;
    case '/intake':     return <IntakePage       key={key} />;
    case '/documents':  return <DocumentsPage    key={key} />;
    case '/document-templates': return <DocumentTemplatesPage key={key} />;
    case '/reports':    return <ReportsPage      key={key} />;
    case '/tasks':      return <TasksPage        key={key} />;
    case '/expenses':   return <ExpensesPage     key={key} />;
    case '/activity':   return <ActivityLogPage  key={key} />;
    case '/deadlines':  return <DeadlinesPage    key={key} />;
    case '/employees':  return <EmployeesPage    key={key} />;
    case '/conflict':   return <ConflictCheckPage key={key} />;
    case '/messages':   return <MessagesPage     key={key} />;
    case '/settings':   return <SettingsPage     key={key} />;
    case '/roles':      return <RolesPage        key={key} />;
    default:            return <DashboardPage    key={key} />;
  }
}

function LoadError({ error, onRetry }) {
  const isTimeout = error?.name === 'TimeoutError';
  const isSetupError = error?.message?.includes('firm') || error?.message?.includes('RLS') || error?.message?.includes('link');
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950 p-4">
      <div className="text-center space-y-4 max-w-md">
        <div className="w-14 h-14 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mx-auto">
          <WifiOff size={24} className="text-red-500" />
        </div>
        <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
          {isTimeout ? 'Connection timed out' : isSetupError ? 'Account setup issue' : 'Could not connect'}
        </h2>
        <p className="text-sm text-slate-500 dark:text-slate-400">
          {isTimeout
            ? 'The server took too long to respond. This can happen when the database is waking up — it usually resolves in a few seconds.'
            : (error?.message || 'Something went wrong loading your account.')}
        </p>
        {isSetupError && (
          <div className="text-left rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 p-4 text-sm text-amber-800 dark:text-amber-200">
            <p className="font-semibold mb-1">Fix: run the repair migration</p>
            <p className="text-xs">In Supabase → SQL Editor, run the file:<br />
              <code className="font-mono bg-amber-100 dark:bg-amber-900/40 px-1 rounded">supabase/migrations/074_repair_broken_accounts.sql</code>
            </p>
            <p className="text-xs mt-2">Then click "Try again" below.</p>
          </div>
        )}
        <button
          onClick={onRetry}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium transition"
        >
          <RefreshCw size={14} /> Try again
        </button>
      </div>
    </div>
  );
}

function AppInner() {
  const { firm, loading, error, refresh, subscriptionStatus } = useFirm();
  const { user, userProfile } = useAuth();
  const { impersonating } = useImpersonation();
  const isImpersonating = !!impersonating;

  // Employee self-service profile wizard state
  const [employeeRecord,    setEmployeeRecord]    = useState(null);  // null = not yet fetched
  const [profileChecked,    setProfileChecked]    = useState(false);
  const [showProfileWizard, setShowProfileWizard] = useState(false);

  // First-time welcome screen (shown once per firm after onboarding wizard finishes).
  const [welcomeDismissed, setWelcomeDismissed] = useState(false);

  // Once the firm is loaded, try to find this user's employee record via a
  // 3-tier waterfall so we handle every possible linking state:
  //   1. employees.user_id = auth UUID          (properly linked)
  //   2. employees.employee_id = users.employee_id  (EMP string set, user_id missing)
  //   3. employees.email = user email           (neither column linked — email match)
  // Firm owners who have no employee row at all simply get null and skip the wizard.
  useEffect(() => {
    if (!firm || !user?.id || isImpersonating) {
      setProfileChecked(true);
      return;
    }

    const resolve = (data) => {
      if (data) {
        setEmployeeRecord(data);
        // Show the profile wizard if:
        //   * profile_completed_at is null (employee never finished it), AND
        //   * they haven't skipped within this browser session
        // The session-only skip means we re-prompt on next login but
        // don't keep showing every page refresh.
        let skipped = false;
        try { skipped = !!sessionStorage.getItem(`lf_profile_wizard_skipped_${data.id}`); }
        catch { /* ignore */ }
        setShowProfileWizard(!data.profile_completed_at && !skipped);
      }
      setProfileChecked(true);
    };

    // Tier 4 (fallback for firm owners / legacy users): if every other
    // tier returns null, call the SECURITY DEFINER RPC that creates the
    // row from the user profile. Skips employee_profile_completed_at
    // initialization so the wizard still prompts them to fill it in.
    const tier4 = async () => {
      const { data: d4, error } = await ensureMyEmployeeRecord();
      if (error) {
        // eslint-disable-next-line no-console
        console.warn('[employee-lookup] ensure_my_employee_record failed:', error.message);
        return null;
      }
      return Array.isArray(d4) ? d4[0] : d4;
    };

    fetchEmployeeByUserId(user.id).then(({ data: d1 }) => {
      if (d1) { resolve(d1); return; }

      // Tier 2: EMP string
      const empIdStr = userProfile?.employee_id;
      if (empIdStr) {
        fetchEmployeeByEmpIdString(empIdStr).then(({ data: d2 }) => {
          if (d2) { resolve(d2); return; }

          // Tier 3: email
          if (user.email) {
            fetchEmployeeByEmail(user.email).then(({ data: d3 }) => {
              if (d3) { resolve(d3); return; }
              tier4().then(resolve);
            });
          } else {
            tier4().then(resolve);
          }
        });
      } else if (user.email) {
        // No EMP string — jump straight to email fallback then tier4.
        fetchEmployeeByEmail(user.email).then(({ data: d3 }) => {
          if (d3) { resolve(d3); return; }
          tier4().then(resolve);
        });
      } else {
        tier4().then(resolve);
      }
    });
    // We intentionally key this effect on stable IDs (firm.id, user.id,
    // employee_id, isImpersonating) rather than the full firm / user
    // objects. Including the full objects would re-run the employee
    // lookup on every background refresh of FirmContext, which would
    // flicker the UI and waste DB calls. The ESLint rule can't see
    // through this so we suppress it deliberately.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firm?.id, user?.id, userProfile?.employee_id, isImpersonating]);

  // Show error screen (with retry) if the initial load failed.
  if (error && !firm) return <LoadError error={error} onRetry={() => refresh()} />;

  // Only blank the screen on the very first load (firm not yet known).
  // Once we have a firm, FirmContext refreshes silently in the background
  // so pages stay mounted and don't lose their data.
  if (loading && !firm) return null;

  // Show onboarding wizard for new firms that haven't completed setup.
  // Skip it if a super-admin is impersonating (they shouldn't be forced through it).
  if (firm && !firm.setup_completed && !isImpersonating) {
    return <FirmOnboardingWizard />;
  }

  // Show the suspended / upgrade page when the trial has expired and they haven't paid.
  // Super-admins impersonating a firm bypass this so they can always inspect.
  if (firm && subscriptionStatus === 'suspended' && !isImpersonating) {
    return <SubscriptionSuspendedPage />;
  }

  // Wait until we know whether to show the profile wizard
  if (userProfile?.employee_id && !profileChecked) return null;

  // Show the employee self-service profile wizard on first login
  if (showProfileWizard && employeeRecord) {
    return (
      <EmployeeProfileWizard
        employee={employeeRecord}
        onComplete={(updatedEmployee) => {
          setEmployeeRecord(updatedEmployee);
          setShowProfileWizard(false);
        }}
      />
    );
  }

  // First-run celebratory welcome — shows once per firm right after the
  // FirmOnboardingWizard sets setup_completed=true. The screen marks itself
  // seen in localStorage so it doesn't return on subsequent logins.
  if (!welcomeDismissed && !isImpersonating && shouldShowPostOnboardingWelcome(firm, userProfile)) {
    return <PostOnboardingWelcome onDismiss={() => setWelcomeDismissed(true)} />;
  }

  return (
    <OnboardingHelpersProvider>
      <TimerProvider>
        <ImpersonationBanner />
        <AppShell employeeRecord={employeeRecord}>
          <AnnouncementsBanner />
          {/* Shown only if this user has an employees row AND hasn't
              finished the profile wizard. Dismissable per session. */}
          <EmployeeProfileReminderBanner employee={employeeRecord} />
          <Router />
        </AppShell>
        <OnboardingHost />
        {/* Global ⌘/Ctrl+K search & navigation. Mounted at the app root
            so it's available from every page. Hidden until hotkey opens it. */}
        <CommandPalette />
        {/* Floating billable timer — pinned to bottom-right whenever a
            timer is running. Renders nothing otherwise. */}
        <FloatingTimer />
      </TimerProvider>
    </OnboardingHelpersProvider>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <FirmProvider>
        <PlanProvider>
          <PermissionsProvider>
            <EncryptionProvider>
              <AppInner />
              <Toaster />
            </EncryptionProvider>
          </PermissionsProvider>
        </PlanProvider>
      </FirmProvider>
    </ErrorBoundary>
  );
}
